import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Disclosure, Dialog, Transition } from '@headlessui/react';
import { XIcon, ExclamationIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { SearchIcon, PlusIcon, MinusIcon, InformationCircleIcon } from '@heroicons/react/solid';
import OrderSearch from './components/OrderSearch';
import ShippingOptions from './components/ShippingOptions';
import { Spinner } from '../../../components/Spinner';
import ProductSelectionModal from '../new-order-components/ProductSelectionModal';
import { useExchangeForm } from './viewmodel/useExchangeForm';
import Modal from '../../../components/Modal';
import { fetchSettings } from '../settings/controller/settings-controller';

const AddExchange = () => {
  const navigate = useNavigate();
  
  // Add application settings state
  const [appSettings, setAppSettings] = useState({
    standardShippingCost: 8, // Default value
  });
  
  // Add useEffect to fetch settings
  useEffect(() => {
    const fetchSettingsApi = async () => {
      try {
        const settings = await fetchSettings();
        console.log('Loaded application settings:', settings.fraisDeLivraison);

        // Check if settings contains shipping costs and update state accordingly
        if (settings) {
          setAppSettings({
            standardShippingCost: settings.fraisDeLivraison || 8,
          });
        }
      } catch (error) {
        console.error('Error loading application settings:', error);
        // Keep default values if there's an error
      }
    };

    fetchSettingsApi();
  }, []);

  const {
    currentStep,
    isLoading,
    isSubmitting,
    selectedOrder,
    exchangeProducts,
    showAddProductModal,
    setShowAddProductModal,
    showConfirmModal,
    setShowConfirmModal,
    customerNote,
    setCustomerNote,
    shippingMethod,
    setShippingMethod,
    isFreeShipping,
    setIsFreeShipping,
    additionalFees,
    setAdditionalFees,
    summary,
    
    handleOrderSelect,
    handleAddToExchange,
    handleRemoveFromExchange,
    handleQuantityChange,
    handleProductSelect,
    handleRemoveProduct,
    handleSubmitExchange,
    handleBack,
    handleNext
  } = useExchangeForm(navigate, appSettings); // Pass appSettings to useExchangeForm

  // Format price
  const formatPrice = (price) => {
    if (price === null || price === undefined) {
      return '0.00 TND';
    }
    return `${parseFloat(price).toFixed(2)} TND`;
  };
  
  console.log('selectedOrder:', selectedOrder);
  // Render different steps based on current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Sélectionner une commande</h2>
              <p className="mt-1 text-sm text-gray-500">
                Recherchez et sélectionnez la commande pour laquelle vous souhaitez effectuer un échange.
              </p>
              <div className="mt-6">
                <OrderSearch onSelectOrder={handleOrderSelect} />
              </div>
            </div>

            {/* Display selected order details - code unchanged */}
            {selectedOrder && (
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Commande #{selectedOrder.number}
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                      {new Date(selectedOrder.date).toLocaleDateString('fr-FR', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-sm font-medium text-gray-500">Total</p>
                    <p className="text-lg font-bold text-gray-900">{formatPrice(selectedOrder.total)}</p>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Client</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {selectedOrder.customer.name}<br />
                        {selectedOrder.customer.email}<br />
                        {selectedOrder.customer.phone}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Adresse de livraison</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {selectedOrder.shippingAddress.street}<br />
                        {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.zipCode}<br />
                        {selectedOrder.shippingAddress.country}
                      </dd>
                    </div>
                  </dl>
                </div>
                
                {/* Desktop Table View */}
                <div className="hidden sm:block border-t border-gray-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Produit
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Prix unitaire
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Quantité
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Total
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedOrder.items.map((item) => (
                          <tr key={item.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <img 
                                    className="h-10 w-10 rounded-md object-cover" 
                                    src={item.image || 'https://www.incathlab.com/images/products/default_product.png'} 
                                    alt={item.name} 
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = 'https://www.incathlab.com/images/products/default_product.png';
                                    }}
                                  />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                  <div className="text-sm text-gray-500">
                                    {item.sku && `${item.sku}`}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatPrice(item.price)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{item.quantity}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatPrice(item.price * item.quantity)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center justify-center">
                                <input
                                  id={`select-product-${item.id}`}
                                  name={`select-product-${item.id}`}
                                  type="checkbox"
                                  className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                                  checked={exchangeProducts.find(p => p.id === item.id)?.selected || false}
                                  onChange={(e) => {
                                    e.stopPropagation(); // Prevent row click from firing
                                    
                                    if (e.target.checked) {
                                      // Add product to exchange
                                      handleAddToExchange(item.id);
                                      
                                      // Scroll to exchange products section
                                      document.getElementById('exchange-products-section')?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start'
                                      });
                                      
                                      // Focus on this product in the exchange list
                                      setTimeout(() => {
                                        const element = document.getElementById(`exchange-item-${item.id}`);
                                        if (element) {
                                          // Apply temporary highlight effect
                                          element.classList.add('bg-indigo-50', 'border-l-4', 'border-indigo-500');
                                          setTimeout(() => {
                                            element.classList.remove('bg-indigo-50', 'border-l-4', 'border-indigo-500');
                                          }, 2000);
                                        }
                                      }, 300);
                                    } else {
                                      // Remove product from exchange
                                      handleRemoveFromExchange(item.id);
                                    }
                                  }}
                                />
                                <label htmlFor={`select-product-${item.id}`} className="ml-2 text-sm text-gray-600 cursor-pointer sr-only">
                                  Ajouter à l'échange
                                </label>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Mobile Card View */}
                <div className="sm:hidden border-t border-gray-200 divide-y divide-gray-200">
                  {selectedOrder.items.map((item) => (
                    <div key={item.id} className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start">
                          <img 
                            className="h-16 w-16 rounded-md object-cover" 
                            src={item.image || 'https://www.incathlab.com/images/products/default_product.png'} 
                            alt={item.name}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://www.incathlab.com/images/products/default_product.png';
                            }}
                          />
                          <div className="ml-3">
                            <div className="font-medium text-gray-900">{item.name}</div>
                            <div className="text-sm text-gray-500 mt-1">
                              {item.sku && `${item.sku}`}
                            </div>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <input
                            id={`mobile-select-product-${item.id}`}
                            name={`mobile-select-product-${item.id}`}
                            type="checkbox"
                            className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                            checked={exchangeProducts.find(p => p.id === item.id)?.selected || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                handleAddToExchange(item.id);
                              } else {
                                handleRemoveFromExchange(item.id);
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Prix:</span> {formatPrice(item.price)}
                        </div>
                        <div className="text-right">
                          <span className="text-gray-500">Quantité:</span> {item.quantity}
                        </div>
                        <div className="col-span-2 text-right font-medium mt-1">
                          <span className="text-gray-500">Total:</span> {formatPrice(item.price * item.quantity)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Configurer l'échange</h2>
              <p className="mt-1 text-sm text-gray-500">
                Définissez les produits à échanger et ceux à ajouter à la nouvelle commande.
              </p>
            </div>

            {/* Original Order Details Section (Collapsible) */}
            <div className="bg-white shadow sm:rounded-lg overflow-hidden">
              <Disclosure defaultOpen={true}>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="w-full px-4 py-5 sm:px-6 text-left flex justify-between items-center bg-gray-50">
                      <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          Commande originale: {selectedOrder.number}
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                          {new Date(selectedOrder.date).toLocaleDateString('fr-FR', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <span className="text-indigo-600 text-sm mr-3">
                          {open ? 'Masquer les détails' : 'Afficher les détails'}
                        </span>
                        <ChevronDownIcon
                          className={`${
                            open ? 'transform rotate-180' : ''
                          } h-5 w-5 text-indigo-500`}
                        />
                      </div>
                    </Disclosure.Button>
                    <Disclosure.Panel>
                      <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Client</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {selectedOrder.customer.name}<br />
                              {selectedOrder.customer.email}<br />
                              {selectedOrder.customer.phone}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Adresse de livraison</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {selectedOrder.shippingAddress.street}<br />
                              {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.zipCode}<br />
                              {selectedOrder.shippingAddress.country}
                            </dd>
                          </div>
                          <div className="sm:col-span-2">
                            <dt className="text-sm font-medium text-gray-500">Résumé de la commande</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              <div className="flex justify-between">
                                <span>Nombre d'articles:</span>
                                <span>{selectedOrder.items.length}</span>
                              </div>
                              <div className="flex justify-between font-medium mt-1">
                                <span>Total:</span>
                                <span>{formatPrice(selectedOrder.total)}</span>
                              </div>
                            </dd>
                          </div>
                        </dl>
                      </div>
                      
                      {/* Desktop Table View - Make it responsive with overflow */}
                      <div className="border-t border-gray-200 hidden sm:block">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th
                                  scope="col"
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Produit
                                </th>
                                <th
                                  scope="col"
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Prix unitaire
                                </th>
                                <th
                                  scope="col"
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Quantité
                                </th>
                                <th
                                  scope="col"
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {selectedOrder.items.map((item) => (
                                <tr key={item.id} 
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => {
                                      // Find the corresponding product in exchangeProducts
                                      const product = exchangeProducts.find(p => p.id === item.id);
                                      if (product) {
                                        // Open a small modal or focus on the product in the exchange list
                                        document.getElementById(`exchange-item-${item.id}`)?.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'center'
                                        });
                                        // Highlight the item temporarily
                                        const element = document.getElementById(`exchange-item-${item.id}`);
                                        if (element) {
                                          element.classList.add('bg-indigo-50', 'border-indigo-500');
                                          setTimeout(() => {
                                            element.classList.remove('bg-indigo-50', 'border-indigo-500');
                                          }, 2000);
                                        }
                                      }
                                    }}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0 h-10 w-10">
                                        <img 
                                          className="h-10 w-10 rounded-md object-cover" 
                                          src={item.image || 'https://www.incathlab.com/images/products/default_product.png'} 
                                          alt={item.name} 
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://www.incathlab.com/images/products/default_product.png';
                                          }}
                                        />
                                      </div>
                                      <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                        <div className="text-sm text-gray-500">
                                          {item.sku && `${item.sku}`}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{formatPrice(item.price)}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{item.quantity}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {formatPrice(item.price * item.quantity)}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center justify-center">
                                      <input
                                        id={`select-product-${item.id}`}
                                        name={`select-product-${item.id}`}
                                        type="checkbox"
                                        className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                                        checked={exchangeProducts.find(p => p.id === item.id)?.selected || false}
                                        onChange={(e) => {
                                          e.stopPropagation(); // Prevent row click from firing
                                          
                                          if (e.target.checked) {
                                            // Add product to exchange
                                            handleAddToExchange(item.id);
                                            
                                            // Scroll to exchange products section
                                            document.getElementById('exchange-products-section')?.scrollIntoView({
                                              behavior: 'smooth',
                                              block: 'start'
                                            });
                                            
                                            // Focus on this product in the exchange list
                                            setTimeout(() => {
                                              const element = document.getElementById(`exchange-item-${item.id}`);
                                              if (element) {
                                                // Apply temporary highlight effect
                                                element.classList.add('bg-indigo-50', 'border-l-4', 'border-indigo-500');
                                                setTimeout(() => {
                                                  element.classList.remove('bg-indigo-50', 'border-l-4', 'border-indigo-500');
                                                }, 2000);
                                              }
                                            }, 300);
                                          } else {
                                            // Remove product from exchange
                                            handleRemoveFromExchange(item.id);
                                          }
                                        }}
                                      />
                                      <label htmlFor={`select-product-${item.id}`} className="ml-2 text-sm text-gray-600 cursor-pointer sr-only">
                                        Ajouter à l'échange
                                      </label>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      
                      {/* Mobile Card View - Move this inside Disclosure.Panel */}
                      <div className="sm:hidden border-t border-gray-200 divide-y divide-gray-200">
                        {selectedOrder.items.map((item) => (
                          <div key={item.id} className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-start">
                                <img 
                                  className="h-16 w-16 rounded-md object-cover" 
                                  src={item.image || 'https://www.incathlab.com/images/products/default_product.png'} 
                                  alt={item.name}
                                />
                                <div className="ml-3">
                                  <div className="font-medium text-gray-900">{item.name}</div>
                                  <div className="text-sm text-gray-500 mt-1">
                                    {item.sku && `${item.sku}`}
                                  </div>
                                </div>
                              </div>
                              <div className="flex-shrink-0">
                                <input
                                  id={`mobile-select-product-${item.id}`}
                                  name={`mobile-select-product-${item.id}`}
                                  type="checkbox"
                                  className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                                  checked={exchangeProducts.find(p => p.id === item.id)?.selected || false}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      handleAddToExchange(item.id);
                                    } else {
                                      handleRemoveFromExchange(item.id);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-gray-500">Prix:</span> {formatPrice(item.price)}
                              </div>
                              <div className="text-right">
                                <span className="text-gray-500">Quantité:</span> {item.quantity}
                              </div>
                              <div className="col-span-2 text-right font-medium mt-1">
                                <span className="text-gray-500">Total:</span> {formatPrice(item.price * item.quantity)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>

            <div id="exchange-products-section" className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Produits de la commande originale</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Cliquez sur les produits de la commande originale pour les ajouter à l'échange.
                </p>
                <div className="mt-5">
                  {exchangeProducts.filter(p => p.originalQuantity > 0 && p.selected).length > 0 ? (
                    <div className="flex flex-col">
                      {/* Desktop Table View */}
                      <div className="hidden sm:block -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                          <div className="overflow-hidden border-b border-gray-200 sm:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    Produit
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    Prix unitaire
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    Quantité d'origine
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    À garder
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    À échanger
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {exchangeProducts.filter(p => p.originalQuantity > 0 && p.selected).map((product) => (
                                  <tr key={product.id} id={`exchange-item-${product.id}`} className="transition-all duration-300">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10">
                                          <img className="h-10 w-10 rounded-md object-cover" src={product.image ||'https://www.incathlab.com/images/products/default_product.png' } alt={product.name} />
                                        </div>
                                        <div className="ml-4">
                                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                          <div className="text-sm text-gray-500">
                                            {product.sku}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">{formatPrice(product.price)}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">{product.originalQuantity}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <button
                                          type="button"
                                          onClick={() => handleQuantityChange(product, 'decrease', 'keep')}
                                          className="text-white bg-gray-400 hover:bg-gray-500 p-1 rounded-l-md"
                                        >
                                          <MinusIcon className="h-4 w-4" />
                                        </button>
                                        <span className="px-4 py-1 bg-gray-100 text-gray-900">
                                          {product.keepQuantity}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => handleQuantityChange(product, 'increase', 'keep')}
                                          className="text-white bg-gray-400 hover:bg-gray-500 p-1 rounded-r-md"
                                        >
                                          <PlusIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">
                                        {product.originalQuantity - product.keepQuantity}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex space-x-2">
                                        <span 
                                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            product.action === 'keep' 
                                              ? 'bg-green-100 text-green-800' 
                                              : product.action === 'exchange' 
                                              ? 'bg-red-100 text-red-800' 
                                              : 'bg-yellow-100 text-yellow-800'
                                          }`}
                                        >
                                          {product.action === 'keep' 
                                            ? 'Gardé' 
                                            : product.action === 'exchange' 
                                            ? 'Échangé' 
                                            : 'Échange partiel'}
                                        </span>
                                        
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      
                      {/* Mobile Card View */}
                      <div className="sm:hidden space-y-3">
                        {exchangeProducts.filter(p => p.originalQuantity > 0 && p.selected).map((product) => (
                          <div key={product.id} id={`exchange-item-${product.id}`} className="bg-white shadow rounded-md p-4 transition-all duration-300">
                            <div className="flex items-start">
                              <img 
                                className="h-16 w-16 rounded-md object-cover" 
                                src={product.image || 'https://www.incathlab.com/images/products/default_product.png'} 
                                alt={product.name}
                              />
                              <div className="ml-3">
                                <div className="font-medium text-gray-900">{product.name}</div>
                                <div className="text-sm text-gray-500 mt-1">{product.sku}</div>
                                <div className="text-sm text-gray-700 mt-1">
                                  <span>Prix: </span>{formatPrice(product.price)}
                                </div>
                              </div>
                            </div>
                            
                            <div className="mt-4 grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Quantité d'origine</label>
                                <div className="text-sm font-medium">{product.originalQuantity}</div>
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">À échanger</label>
                                <div className="text-sm font-medium">{product.originalQuantity - product.keepQuantity}</div>
                              </div>
                              
                              <div className="col-span-2 mt-1">
                                <label className="block text-xs font-medium text-gray-500 mb-1">À garder</label>
                                <div className="flex items-center">
                                  <button
                                    type="button"
                                    onClick={() => handleQuantityChange(product, 'decrease', 'keep')}
                                    className="text-white bg-gray-400 hover:bg-gray-500 p-1 rounded-l-md"
                                  >
                                    <MinusIcon className="h-4 w-4" />
                                  </button>
                                  <span className="px-4 py-1 bg-gray-100 text-gray-900">
                                    {product.keepQuantity}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleQuantityChange(product, 'increase', 'keep')}
                                    className="text-white bg-gray-400 hover:bg-gray-500 p-1 rounded-r-md"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                            
                            <div className="mt-3">
                              <span 
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  product.action === 'keep' 
                                    ? 'bg-green-100 text-green-800' 
                                    : product.action === 'exchange' 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {product.action === 'keep' 
                                  ? 'Gardé' 
                                  : product.action === 'exchange' 
                                  ? 'Échangé' 
                                  : 'Échange partiel'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-8 text-center rounded-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2H5a2 2 0 00-2 2v2M7 7h10" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun produit sélectionné</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Cliquez sur un produit dans la commande originale pour l'ajouter à l'échange.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Nouveaux produits à ajouter</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddProductModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                    Ajouter un produit
                  </button>
                </div>
                <div className="mt-5">
                  {exchangeProducts.some(p => p.action === 'add') ? (
                    <div className="flex flex-col">
                      {/* Desktop Table View */}
                      <div className="hidden sm:block -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                          <div className="overflow-hidden border-b border-gray-200 sm:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    Produit
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    Prix unitaire
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    Quantité
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    Total
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {exchangeProducts.filter(p => p.action === 'add').map((product) => (
                                  <tr key={`new-${product.id}`}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10">
                                          <img className="h-10 w-10 rounded-md object-cover" src={product.image} alt={product.name} />
                                        </div>
                                        <div className="ml-4">
                                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                          <div className="text-sm text-gray-500">
                                            {product.sku}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">{formatPrice(product.price)}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">{product.quantity}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm font-medium text-gray-900">
                                        {formatPrice(product.price * product.quantity)}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveProduct(product.id)}
                                        className="text-red-600 hover:text-red-900"
                                      >
                                        Supprimer
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      
                      {/* Mobile Card View */}
                      <div className="sm:hidden space-y-3">
                        {exchangeProducts.filter(p => p.action === 'add').map((product) => (
                          <div key={`new-${product.id}`} className="bg-white shadow rounded-md p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start">
                                <img 
                                  className="h-16 w-16 rounded-md object-cover" 
                                  src={product.image} 
                                  alt={product.name}
                                />
                                <div className="ml-3">
                                  <div className="font-medium text-gray-900">{product.name}</div>
                                  <div className="text-sm text-gray-500 mt-1">{product.sku}</div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveProduct(product.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <XIcon className="h-5 w-5" />
                              </button>
                            </div>
                            
                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-gray-500">Prix:</span> {formatPrice(product.price)}
                              </div>
                              <div className="text-right">
                                <span className="text-gray-500">Quantité:</span> {product.quantity}
                              </div>
                              <div className="col-span-2 text-right font-medium border-t border-gray-100 pt-2 mt-1">
                                <span className="text-gray-500">Total:</span> {formatPrice(product.price * product.quantity)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-8 text-center rounded-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun nouveau produit</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Ajoutez de nouveaux produits pour compléter l'échange.
                      </p>
                      <div className="mt-6">
                        <button
                          type="button"
                          onClick={() => setShowAddProductModal(true)}
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                          Ajouter un produit
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Détails de l'échange</h3>
                <div className="mt-5 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <div className="sm:col-span-6">
                    <label htmlFor="customer-note" className="block text-sm font-medium text-gray-700">
                      Note du client (optionnel)
                    </label>
                    <div className="mt-1">
                      <textarea
                        id="customer-note"
                        name="customer-note"
                        rows={3}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="Ajoutez des notes supplémentaires fournies par le client"
                        value={customerNote}
                        onChange={(e) => setCustomerNote(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Finaliser l'échange</h2>
              <p className="mt-1 text-sm text-gray-500">
                Vérifiez les détails et configurez les options d'expédition et de paiement.
              </p>
            </div>

            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Options d'expédition</h3>
                <ShippingOptions 
                  shippingMethod={shippingMethod}
                  setShippingMethod={setShippingMethod}
                  isFreeShipping={isFreeShipping}
                  setIsFreeShipping={setIsFreeShipping}
                  appSettings={appSettings} // Pass appSettings to ShippingOptions
                />
              </div>
            </div>

            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Frais supplémentaires</h3>
                <div className="mt-5">
                  <label htmlFor="additional-fees" className="block text-sm font-medium text-gray-700">
                    Montant (TND)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">TND</span>
                    </div>
                    <input
                      type="number"
                      name="additional-fees"
                      id="additional-fees"
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-12 pr-12 sm:text-sm border-gray-300 rounded-md"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={additionalFees}
                      onChange={(e) => setAdditionalFees(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Résumé de l'échange</h3>
                <div className="mt-5 border-t border-gray-200 pt-4">
                  <dl className="divide-y divide-gray-200">
                    <div className="py-3 flex flex-wrap justify-between text-sm font-medium">
                      <dt className="text-gray-500 w-1/2">Montant commande originale</dt>
                      <dd className="text-gray-900 w-1/2 text-right">{formatPrice(summary.originalTotal)}</dd>
                    </div>
                    <div className="py-3 flex flex-wrap justify-between text-sm font-medium">
                      <dt className="text-gray-500 w-1/2">Produits conservés</dt>
                      <dd className="text-gray-900 w-1/2 text-right">{formatPrice(summary.keepingTotal || 0)}</dd>
                    </div>
                    <div className="py-3 flex flex-wrap justify-between text-sm font-medium">
                      <dt className="text-gray-500 w-1/2">Nouveaux produits</dt>
                      <dd className="text-gray-900 w-1/2 text-right">{formatPrice(summary.newProductsTotal || 0)}</dd>
                    </div>
                    <div className="py-3 flex flex-wrap justify-between text-sm font-medium">
                      <dt className="text-gray-500 w-1/2">Frais de livraison</dt>
                      <dd className="text-gray-900 w-1/2 text-right">{formatPrice(summary.shippingCost || 0)}</dd>
                    </div>
                    <div className="py-3 flex flex-wrap justify-between text-sm font-medium">
                      <dt className="text-gray-500 w-1/2">Frais supplémentaires</dt>
                      <dd className="text-gray-900 w-1/2 text-right">{formatPrice(summary.additionalFees)}</dd>
                    </div>
                    <div className="py-3 flex flex-wrap justify-between text-sm font-medium">
                      <dt className="text-gray-900 font-bold w-1/2">Nouveau total</dt>
                      <dd className="text-gray-900 font-bold w-1/2 text-right">{formatPrice(summary.newTotal)}</dd>
                    </div>
                    <div className="py-3 flex flex-wrap justify-between text-sm font-medium">
                      <dt className="text-gray-900">Différence</dt>
                      <dd className={`font-medium w-1/2 text-right ${summary.difference > 0 ? 'text-green-600' : summary.difference < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                        {summary.difference > 0 ? '+' : ''}{formatPrice(summary.difference)}
                      </dd>
                    </div>
                  </dl>
                </div>
                
                {summary.difference !== 0 && (
                  <div className={`mt-4 rounded-md ${summary.difference > 0 ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'} p-4`}>
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <InformationCircleIcon className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium">
                          {summary.difference > 0 
                            ? 'Paiement supplémentaire requis' 
                            : 'Remboursement nécessaire'}
                        </h3>
                        <div className="mt-2 text-sm">
                          <p>
                            {summary.difference > 0 
                              ? `Le client devra payer un montant supplémentaire de ${formatPrice(summary.difference)}.` 
                              : `Le client devra être remboursé de ${formatPrice(Math.abs(summary.difference))}.`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      {/* Page header with improved responsiveness */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Nouvel Échange</h1>
        <p className="mt-1 text-sm text-gray-700">
          Créez un échange pour une commande existante en quelques étapes simples.
        </p>
      </div>

      {/* Progress steps with better mobile display */}
      <nav aria-label="Progress" className="mb-6 sm:mb-12">
        <ol className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between">
          <li className="relative flex items-center mb-8 sm:mb-0 sm:w-1/3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                currentStep === 1 
                  ? 'bg-indigo-600 text-white' 
                  : currentStep > 1 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white border-2 border-gray-300 text-gray-500'
              }`}
            >
              {currentStep > 1 ? (
                <CheckIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <span>1</span>
              )}
            </div>
            <span className={`ml-3 text-sm font-medium ${
              currentStep === 1 ? 'text-indigo-600' : currentStep > 1 ? 'text-indigo-600' : 'text-gray-500'
            }`}>
              Sélection de commande
            </span>
            
            {/* Modified connector */}
            {currentStep > 1 && (
              <div className="hidden sm:block absolute right-0 h-0.5 w-1/3 bg-indigo-600" style={{ left: '70%' }} aria-hidden="true" />
            )}
            {currentStep <= 1 && (
              <div className="hidden sm:block absolute right-0 h-0.5 w-1/3 bg-gray-200" style={{ left: '70%' }} aria-hidden="true" />
            )}
          </li>

          <li className="relative flex items-center mb-8 sm:mb-0 sm:w-1/3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                currentStep === 2 
                  ? 'bg-indigo-600 text-white' 
                  : currentStep > 2 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white border-2 border-gray-300 text-gray-500'
              }`}
            >
              {currentStep > 2 ? (
                <CheckIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <span>2</span>
              )}
            </div>
            <span className={`ml-3 text-sm font-medium ${
              currentStep === 2 ? 'text-indigo-600' : currentStep > 2 ? 'text-indigo-600' : 'text-gray-500'
            }`}>
              Configuration de l'échange
            </span>
            
            {/* Modified connector */}
            {currentStep > 2 && (
              <div className="hidden sm:block absolute right-0 h-0.5 w-1/3 bg-indigo-600" style={{ left: '70%' }} aria-hidden="true" />
            )}
            {currentStep <= 2 && (
              <div className="hidden sm:block absolute right-0 h-0.5 w-1/3 bg-gray-200" style={{ left: '70%' }} aria-hidden="true" />
            )}
          </li>

          <li className="relative flex items-center sm:w-1/3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                currentStep === 3 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white border-2 border-gray-300 text-gray-500'
              }`}
            >
              <span>3</span>
            </div>
            <span className={`ml-3 text-sm font-medium ${
              currentStep === 3 ? 'text-indigo-600' : 'text-gray-500'
            }`}>
              Finalisation
            </span>
          </li>
        </ol>
      </nav>

      {/* Main content */}
      <div className="mt-10 md:mt-0">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="space-y-8">
            {renderStep()}

            <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={currentStep === 1 ? () => navigate('/admin/exchanges') : handleBack}
                className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {currentStep === 1 ? 'Annuler' : 'Retour'}
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={
                  currentStep === 1 && !selectedOrder
                }
                className={`inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  currentStep === 1 && !selectedOrder
                    ? 'bg-indigo-300 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
              >
                {currentStep === 3 ? 'Créer l\'échange' : 'Continuer'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Product selection modal */}
      <ProductSelectionModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        onProductSelect={handleProductSelect}
        allowOutOfStock={false}
      />

      {/* Confirmation modal using your custom Modal component */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmer l'échange"
        onSubmit={handleSubmitExchange}
        submitLabel={isSubmitting ? "En cours..." : "Confirmer"}
        isSubmitting={isSubmitting}
        cancelLabel="Annuler"
        size="md"
      >
        <div className="py-2">
          <p className="text-sm text-gray-500">
            Êtes-vous sûr de vouloir créer cet échange? Cette action ne peut pas être annulée.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default AddExchange;